import { useState } from 'react';
import { Alert, Button, Card, CardContent, TextField } from '@mui/material';
import type { PublishingIntegrationView } from '../../../api/src/telegram/publishing.types';
import { api } from '../api/client';
import { Stack, Typography } from '../components/mui-system';

export function PublishingSourceSettings({
  workspaceId,
  value,
  canEdit,
  onChanged,
}: {
  workspaceId: string;
  value: PublishingIntegrationView;
  canEdit: boolean;
  onChanged: () => Promise<void>;
}) {
  const [address, setAddress] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function save(sourceUrl: string) {
    setBusy(true);
    setError('');
    try {
      await api.publishingIntegration(workspaceId, sourceUrl, value.revision);
      setAddress(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось проверить источник');
    } finally {
      try {
        await onChanged();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось обновить состояние подключения');
      }
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardContent>
        <Stack gap={2}>
          <Typography variant="h6">Источник API публикаций</Typography>
          <Typography>Текущий домен: {value.sourceUrl ?? 'не настроен'}</Typography>
          <Typography color="text.secondary">
            Последняя успешная проверка:{' '}
            {value.checkedAt
              ? new Date(value.checkedAt).toLocaleString('ru-RU')
              : 'ещё не выполнена'}
          </Typography>
          {value.error && (
            <Alert severity="error">
              {value.attemptedUrl ? `${value.attemptedUrl}: ` : ''}
              {value.error}
            </Alert>
          )}
          {error && error !== value.error && <Alert severity="error">{error}</Alert>}
          {canEdit && (
            <>
              <TextField
                label="Домен API"
                type="url"
                value={address ?? value.sourceUrl ?? ''}
                onChange={(e) => setAddress(e.target.value)}
                disabled={busy}
                helperText="Общий HTTPS-домен для всех каналов этого рабочего пространства."
                fullWidth
              />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  disabled={busy || !(address ?? value.sourceUrl)?.trim()}
                  onClick={() => void save(address ?? value.sourceUrl ?? '')}
                >
                  {busy ? 'Проверка источника…' : 'Проверить и сохранить источник'}
                </Button>
                {value.candidateUrl && (
                  <Button
                    disabled={busy}
                    onClick={() => {
                      setAddress(value.candidateUrl);
                      void save(value.candidateUrl!);
                    }}
                  >
                    Проверить и использовать {value.candidateUrl}
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
